import { Currencies, DEPOSIT_ADDRESS_TYPE } from "@letsbit/malcolmlb";
import { RecepitPerson } from "@letsbit/milkylb/lib/components/NewReceipt/types";
import dayjs from "dayjs";
import { useMemo } from "react";
import { defineMessages, useIntl } from "react-intl";

import { capitalize } from "utils/capitalize";
import { useFormatter } from "../../../../../hooks/utils/useFormatter";
import { useGetDepositAddress } from "hooks/payments/useGetDepositAddress";
import { useGetMemberStatus } from "hooks/compliance/useGetMemberStatus";
import { HistoryDownloadReceiptProps, RECEIPT_TYPE } from "./types";

export const scope = "hooks.useGetDataForReceipt";

export const messages = defineMessages({
  preNameFrom: {
    id: `${scope}.preNameFrom`,
    defaultMessage: "De",
  },
  preNameTo: {
    id: `${scope}.preNameTo`,
    defaultMessage: "Para",
  },
  sendTransferTo: {
    id: `${scope}.sendTransferTo`,
    defaultMessage: "Transferencia enviada ",
  },
  getMoneyFrom: {
    id: `${scope}.getMoneyFrom`,
    defaultMessage: "Recibiste",
  },
  withholdingTitle: {
    id: `${scope}.withholdingTitle`,
    defaultMessage: "Suma retenida",
  },
  withholdingPersonNID: {
    id: `${scope}.withholdingPersonNID`,
    defaultMessage: "Sujeto retenido",
  },
  aliWithholding: {
    id: `${scope}.aliWithholding`,
    defaultMessage: "Alícuota",
  },
  baseWithholding: {
    id: `${scope}.baseWithholding`,
    defaultMessage: "Base imponible",
  },
  linkedTid: {
    id: `${scope}.linkedTid`,
    defaultMessage: "ID Mov. Vinculado",
  },
  deposit: {
    id: `${scope}.deposit`,
    defaultMessage: "Depósito",
  },
  withdraw: {
    id: `${scope}.withdraw`,
    defaultMessage: "Envío",
  },
  cbuCvu: {
    id: `${scope}.cbuCvu`,
    defaultMessage: "CBU/CVU",
  },
  cbu: {
    id: `${scope}.cbu`,
    defaultMessage: "CBU",
  },
  cvu: {
    id: `${scope}.cvu`,
    defaultMessage: "CVU",
  },
  cuit: {
    id: `${scope}.cuit`,
    defaultMessage: "CUIT",
  },
  cuil: {
    id: `${scope}.cuil`,
    defaultMessage: "CUIL",
  },
  orderTo: {
    id: `${scope}.orderTo`,
    defaultMessage: "A orden de:",
  },
  id: {
    id: `${scope}.id`,
    defaultMessage: "ID movimiento",
  },
  share: {
    id: `${scope}.share`,
    defaultMessage: "COMPARTIR",
  },
  shareTitle1: {
    id: `${scope}.shareTitle1`,
    defaultMessage: "Comprobante transferencia",
  },
  shareTitle2: {
    id: `${scope}.shareTitle2`,
    defaultMessage: "LB Finanzas",
  },
  shareText: {
    id: `${scope}.shareText`,
    defaultMessage:
      "Te comparto el comprobante de la transferencia realizada en LB Finanzas",
  },
  filename: {
    id: `${scope}.filename`,
    defaultMessage: "Comprobante LetsBit",
  },
  coelsaID: {
    id: `${scope}.coelsaID`,
    defaultMessage: "ID COELSA",
  },
  entity: {
    id: `${scope}.entity`,
    defaultMessage: "Entidad",
  },
  generating: {
    id: `${scope}.generating`,
    defaultMessage: "Generando",
  },
  concept: {
    id: `${scope}.concept`,
    defaultMessage: "Concepto",
  },
  transferType: {
    id: `${scope}.transferType`,
    defaultMessage: "Tipo de transferencia",
  },
  send: {
    id: `${scope}.send`,
    defaultMessage: "Enviar",
  },
  var: {
    id: `${scope}.var`,
    defaultMessage: "Varios",
  },
  funds: {
    id: `${scope}.funds`,
    defaultMessage: "Fondos a utilizar",
  },
});

export const useGetDataForReceipt = ({
  receiptType,
  movement,
}: HistoryDownloadReceiptProps) => {
  const intl = useIntl();
  const { formatValue } = useFormatter();
  const { data: memberStatus } = useGetMemberStatus();
  const { data: depositAddress } = useGetDepositAddress(
    movement?.currency_id as Currencies,
    receiptType === RECEIPT_TYPE.DEPOSIT ||
      receiptType === RECEIPT_TYPE.WITHDRAW
      ? movement?.extra_data?.payment_network_id
      : "",
  );

  const { tid, txid } = useMemo(() => {
    return {
      tid: movement?.tid ?? "",
      txid:
        receiptType === RECEIPT_TYPE.DEPOSIT ||
        receiptType === RECEIPT_TYPE.WITHDRAW
          ? movement.extra_data?.txid ?? intl.formatMessage(messages.generating)
          : "",
    };
  }, [intl, movement.extra_data, movement?.tid, receiptType]);

  const title = useMemo(() => {
    switch (receiptType) {
      case RECEIPT_TYPE.DEPOSIT:
        return intl.formatMessage(messages.getMoneyFrom);
      case RECEIPT_TYPE.WITHDRAW:
        return intl.formatMessage(messages.sendTransferTo);
      case RECEIPT_TYPE.WITHHOLDING:
      case RECEIPT_TYPE.WITHHOLDINGS_WITHHOLDING:
        return intl.formatMessage(messages.withholdingTitle);
      default:
        return "";
    }
  }, [intl, receiptType]);

  const date = useMemo(() => {
    return dayjs(movement?.date).format("HH:mm DD.MM.YYYY");
  }, [movement?.date]);

  const amount = useMemo(() => {
    return `${formatValue(movement?.amount || "0")} ${
      movement?.ticker_id.toUpperCase() || "-"
    }`;
  }, [formatValue, movement?.amount, movement?.ticker_id]);

  const from: RecepitPerson = useMemo(() => {
    let cuit: string | undefined;
    switch (receiptType) {
      case RECEIPT_TYPE.DEPOSIT:
        if (movement.currency_id === ("usdeeuu" as Currencies)) {
          return {
            preName: intl.formatMessage(messages.preNameFrom),
            name: movement.extra_data.user_full_name ?? "",
            extraData: [
              {
                label: intl.formatMessage(messages.transferType),
                value: movement.extra_data.payment_network?.name ?? "",
              },
            ],
          };
        }

        return {
          preName: intl.formatMessage(messages.preNameFrom),
          name: movement.extra_data.user_full_name ?? "",
          extraData: [
            {
              label: intl.formatMessage(messages.cbuCvu),
              value: movement.extra_data.address ?? "",
            },
            {
              label: intl.formatMessage(messages.cuit),
              value: movement.extra_data.nid ?? "",
            },
          ],
        };
      case RECEIPT_TYPE.WITHDRAW:
        if (depositAddress?.type === DEPOSIT_ADDRESS_TYPE.OMNIBUS_ACCOUNT) {
          return {
            preName: intl.formatMessage(messages.preNameFrom),
            name:
              depositAddress.data.find(
                (item) => item.key.toUpperCase() === "RAZÓN SOCIAL",
              )?.value || "-",
            extraData: [
              {
                label: intl.formatMessage(messages.cbu),
                value:
                  depositAddress.data.find(
                    (item) => item.key.toUpperCase() === "CBU",
                  )?.value || "-",
              },
              {
                label: intl.formatMessage(messages.cuit),
                value:
                  depositAddress.data.find(
                    (item) => item.key.toUpperCase() === "CUIT",
                  )?.value || "-",
              },
              {
                label: intl.formatMessage(messages.orderTo),
                value: `${memberStatus?.natural_person_data?.first_name || ""} ${
                  memberStatus?.natural_person_data?.last_name || ""
                }`,
              },
              {
                label: intl.formatMessage(messages.cuil),
                value: memberStatus?.customer?.national_id || "",
              },
            ],
          };
        } else {
          return {
            preName: intl.formatMessage(messages.preNameFrom),
            name: `${capitalize(
              memberStatus?.natural_person_data?.first_name || "",
            )} ${capitalize(memberStatus?.natural_person_data?.last_name)}`,
            extraData: [
              {
                label: intl.formatMessage(messages.cvu),
                value: depositAddress?.data.address || "",
              },
              {
                label: intl.formatMessage(messages.cuit),
                value: memberStatus?.customer?.national_id || "",
              },
            ].filter((item) => item.value !== ""),
          };
        }
      case RECEIPT_TYPE.WITHHOLDINGS_WITHHOLDING:
        cuit = movement.extra_data.national_id_target;
        return {
          preName: intl.formatMessage(messages.withholdingPersonNID),
          name:
            cuit && cuit?.length >= 11
              ? cuit.slice(0, 2) +
                "-" +
                cuit.slice(2, -1) +
                "-" +
                cuit.slice(-1)
              : cuit || "",
          extraData: [
            {
              label: intl.formatMessage(messages.aliWithholding),
              value: `${+(movement.extra_data.tax?.amount || "0") * 100} %`,
            },
            {
              label: intl.formatMessage(messages.baseWithholding),
              value: `${formatValue(
                movement.extra_data.base_amount || "",
                movement.precision,
              )} ${movement.ticker_id.toUpperCase()}`,
            },
          ],
        };
      case RECEIPT_TYPE.WITHHOLDING:
      default:
        return {
          preName: "",
          name: "",
          extraData: [{ label: "", value: "" }],
        };
    }
  }, [
    depositAddress,
    formatValue,
    intl,
    memberStatus?.customer?.national_id,
    memberStatus?.natural_person_data?.first_name,
    memberStatus?.natural_person_data?.last_name,
    movement.currency_id,
    movement.extra_data,
    movement.precision,
    movement.ticker_id,
    receiptType,
  ]);

  const to = useMemo(() => {
    return receiptType === RECEIPT_TYPE.WITHDRAW
      ? {
          preName: intl.formatMessage(messages.preNameTo),
          name: movement.extra_data.user_full_name || "-",
          extraData: [
            {
              label: intl.formatMessage(messages.cbuCvu),
              value: movement.extra_data.address || "-",
            },
            {
              label: intl.formatMessage(messages.cuit),
              value: movement.extra_data.nid || "-",
            },
          ],
        }
      : undefined;
  }, [intl, movement.extra_data, receiptType]);

  const showCoelsaId = useMemo(() => {
    const curr = movement.currency_id;
    return (
      curr !== "usd" &&
      curr !== ("usdeeuu" as Currencies) &&
      (receiptType === RECEIPT_TYPE.WITHDRAW ||
        receiptType === RECEIPT_TYPE.DEPOSIT)
    );
  }, [movement.currency_id, receiptType]);

  const extras = useMemo(() => {
    let data: {
      label: string;
      value: string;
    }[] = [];

    switch (receiptType) {
      case RECEIPT_TYPE.DEPOSIT:
        // TODO: Add concept to deposit and withdraw
        data = data.concat([
          {
            value: movement.extra_data.bank_legal_name ?? "",
            label: intl.formatMessage(messages.entity),
          },
        ]);
        break;
      case RECEIPT_TYPE.WITHDRAW:
        // TODO: Add concept to deposit and withdraw
        data = data.concat([
          {
            value: movement.extra_data.bank_legal_name ?? "",
            label: intl.formatMessage(messages.entity),
          },
        ]);
        break;
      case RECEIPT_TYPE.WITHHOLDING:
        break;
      case RECEIPT_TYPE.WITHHOLDINGS_WITHHOLDING:
        data = data.concat([
          {
            value: movement.extra_data.external_id || "",
            label: intl.formatMessage(messages.linkedTid),
          },
        ]);
        break;
      default:
        break;
    }

    data = data.concat([
      {
        value: tid,
        label: intl.formatMessage(messages.id),
      },
    ]);

    if (showCoelsaId) {
      data = data.concat([
        {
          value: txid,
          label: intl.formatMessage(messages.coelsaID),
        },
      ]);
    }

    return data;
  }, [intl, movement.extra_data, receiptType, showCoelsaId, tid, txid]);

  const showSkeleton = useMemo(() => {
    return !memberStatus;
  }, [memberStatus]);

  return { title, date, amount, from, to, extras, showSkeleton };
};
